/**
 * An album page or a Recently Played entry keyed by a Qobuz TRACK id.
 *
 * Paul McGowan, Oct 26 2026: "Zoot Sims And The Gershwin Brothers" sat in
 * Recently Played as 54335098 (a track id) instead of oi3ttwef8h75a, so the
 * album page fetched a non-existent album and said "This album is not available
 * for streaming." while the mini player was playing it.
 *
 * Qobuz track ids are 5–10 digits; numeric Qobuz album ids are 13-digit UPCs,
 * other album ids are alphanumeric. A short all-digit id is only a SUSPECT —
 * callers try it as an album first and resolve it as a track only on failure.
 */
import { CLOUD_API_URL } from '../config/serverConfig';

const TRACK_ID_SHAPE = /^[0-9]{5,10}$/;

export function isTrackIdShapedAlbumId(id: unknown): boolean {
  return TRACK_ID_SHAPE.test(String(id ?? '').trim());
}

/** Qobuz track id → album id via mycelium; null when it is not a track or the lookup fails. */
export async function resolveQobuzAlbumIdForTrackId(
  trackId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<string | null> {
  const id = String(trackId || '').trim();
  if (!isTrackIdShapedAlbumId(id)) return null;
  try {
    const resp = await fetchImpl(
      `${CLOUD_API_URL}/api/streaming/qobuz/track-album?track_id=${encodeURIComponent(id)}`,
    );
    if (!resp.ok) return null;
    const body = await resp.json().catch(() => null);
    const albumId = String(body?.albumId || '').trim().replace(/^qobuz:album:/i, '');
    return albumId && albumId !== id ? albumId : null;
  } catch {
    return null;
  }
}

/**
 * The album handed to play/queue handlers: once the real Qobuz album is known,
 * a page opened under a track id (or with no id) must not stamp that id onto
 * queued tracks — they feed Recently Played.
 */
export function albumForPlayback<T extends Record<string, any> | null | undefined>(
  album: T,
  qobuzAlbumIdLoaded: unknown,
  isLocal: boolean,
): T {
  if (isLocal || !album) return album;
  const realId = String(qobuzAlbumIdLoaded ?? '').trim().replace(/^qobuz:album:/i, '');
  if (!realId) return album;
  const currentId = String(album.id ?? '').trim();
  if (currentId === realId) return album;
  if (album.source === 'qobuz' || !currentId || isTrackIdShapedAlbumId(currentId)) {
    return { ...album, id: realId, serviceAlbumId: realId, source: 'qobuz' };
  }
  return album;
}
